import { HttpHeaders, HttpParams } from '@angular/common/http';
import { IGroup } from './group/group.structure';
import { ICompany } from '@company/company.structures';
import { EMPTY } from '@utils';
import { MatSnackBarConfig } from '@angular/material';

export interface IRole {
  id?: number;
  name: string;
  code: string;
  is_deletable?: boolean;
}

// --------------------------------------------------------------------
export interface IErrorResponse {
  title: string;
  message: string;
}

export interface two_factor_otp {
  otp_counter: number;
  otp_expired_at: Date;
  require_two_factor: boolean;
  send_email: boolean;
}

export interface IOTP {
  email:string;
  verify_key:string;
  otp_counter:number;
  error_message?: string;
}

// --------------------------------------------------------------------
export interface IUser {
  id?: number;
  password: string;
  last_login?: Date;
  is_superuser: boolean;
  username: string;
  first_name: string;
  last_name: string;
  is_active: Boolean;
  date_joined?: Date;
  email: string;
  role: any;
  companies: object[];
  memberships: object[];
  tableau_id: string;
  tableau_license_type: string;
  // two_factors
  two_factor_auth?: two_factor_otp
}

export class User implements IUser {
  password = '';
  is_active = false;
  is_superuser = false;
  username = '';
  first_name = '';
  last_name = '';
  email = '';
  role = null;
  companies = [];
  memberships = [];
  tableau_id = EMPTY;
  tableau_license_type = EMPTY;

  constructor(values: Object = {}) {
    Object.assign(this, values);
  }
}

export interface IUserList {
  count: number;
  next: string;
  previous: string;
  results: Array<IUser>;
}

// --------------------------------------------------------------------
export interface IDialogData {
  strQuestion: string;
  strAccept: string;
  strCancel: string;
  buttonColor: string;
}

// --------------------------------------------------------------------
export interface ICheckListBoardOptions {
  id?: number;
  name?: string;
  selected?: boolean;
}

// --------------------------------------------------------------------
export interface IDialogSearchParams {
  singleName: string;
  filter: string;
  searchKey: string;
  exclude: any[];
  source: [];
  onlySource: boolean;
}

// --------------------------------------------------------------------
export interface ICheckListParams {
  title: string;
  description: string;
  icon: string;
  gettingData: boolean;
  footer: string;
  relatedName: string;
  expanded: boolean;
  searchButton: boolean;
  autoActivate: boolean;
  listType?: string;
  id?: number;
  searchParams?: IDialogSearchParams;
  total: object;
  showDescription: boolean;
}

export interface ICheckListBoard {
  info: Partial<ICheckListParams>;
  data: ICheckListBoardOptions[];
}

// --------------------------------------------------------------------
export interface ICompanyUser {
  id: number;
  company_id: number;
  user_id: number;
  active: boolean;
  user?: IUser;
}

export interface ICompanyModule {
  id?: number;
  active: boolean;
  module: IModule;
}

export interface IUserCompany {
  id?: number;
  active: boolean;
  company: Partial<ICompany>;
}

// --------------------------------------------------------------------
export interface ICompanyGroup {
  active: boolean;
  group: IGroup;
}

// --------------------------------------------------------------------
export interface IMember {
  id: number;
  active: boolean;
  group_id: number;
  user: IUser;
}

export interface IChartDataset {
  label: string;
  backgroundColor: string[];
  borderColor: string[];
  borderWidth: number;
  data: number[];
  fill: boolean;
  pointRadius?: number;
  pointHoverRadius?: number;
  type?: string;
  lineTension?: number;
}
export interface IChartData {
  labels: string[];
  datasets: IChartDataset[];
}
// --------------------------------------------------------------------
export interface IChartConfig {
  type: string;
  data: IChartData;
  options: object;
}

// --------------------------------------------------------------------
export interface ICompanySelectorDialog {
  companies: ICompany[];
}

// --------------------------------------------------------------------
export interface ILogger {
  id: number;
  msg: string;
  action: string;
  level: string;
  created_at: Date;
  company_id: number;
  user_id: number;
  entity: string;
  role: string;
}

// --------------------------------------------------------------------
export interface ICompanyUserRelated {
  id?: number;
  user: Partial<IUser>;
  active: boolean;
  relationshipId?: number;
}

// --------------------------------------------------------------------
export interface ISummaryCard {
  icon: string;
  title: string;
  subtitle: string;
  link: string;
}

// --------------------------------------------------------------------
export interface IModule {
  id?: number;
  name: string;
  slug: string;
  created_at?: Date;
  updated_at?: Date;
  tableau_synchronize?: boolean;
  tag: string;
  companies: object[];
  only_admin: boolean;
}

// --------------------------------------------------------------------
export interface ITableauUser {
  id: string;
  name: string;
  site_role: string;
}

// --------------------------------------------------------------------
export interface ITableauProject {
  id: string;
  name: string;
}

// --------------------------------------------------------------------
export interface ITableauWorkbook {
  id: string;
  name: string;
  contentUrl: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  encryptExtracts: boolean;
  showTabs: boolean;
  tags: [];
  webpageUrl: string;
  project: object;
  owner: object;
  project_name: string;
  project_id: string;
  company_id: any;
  tableau_id: string;
  data_url?: string;
  thumbnail?: string;
}

// --------------------------------------------------------------------
export interface ITableauLink {
  id?: number;
  group_id: number;
  workbook_id: number | string;
  group_name?: string;
  workbook_name?: string;
}

// --------------------------------------------------------------------
export interface ITableauGroup {
  id?: any;
  name: string;
  domain_name?: string;
  company_id?: number;
}

// --------------------------------------------------------------------
export interface IWorkbook {
  company_id: number;
  content_url: string;
  created_at: string;
  email: string;
  first_name: string;
  id: number;
  last_name: string;
  name: string;
  show_tabs: boolean;
  size: number;
  tableau_id: string;
  updated_at: string;
  user_id: number;
  user_tableau_id: string;
  webpage_url: string;
  tags?: [];
  thumbnail?: string;
  open_as?: string;
}
export class Workbook implements IWorkbook {
  company_id: number;
  show_tabs = true;
  size: number;
  user: IUser = null;
  workbook: IWorkbook = null;
  content_url = '';
  created_at = '';
  email = '';
  first_name = '';
  id: number = null;
  last_name = '';
  name = '';
  tableau_id = '';
  updated_at: string;
  user_id: number;
  user_tableau_id: string;
  webpage_url: string;
  tags?: [];
  thumbnail?: string;
  open_as?: string;

  constructor(values: Object = {}) {
    Object.assign(this, values);
  }
}

// --------------------------------------------------------------------
export interface ITag {
  id?: number;
  name: string;
  company_id?: number;
  tableau_id?: string;

  created_at?: Date;
  updated_at?: Date;
  company?: any;
}

// --------------------------------------------------------------------
export interface IDeviceInfo {
  browser: string;
  device: string;
  os: string;
  os_version: string;
  userAgent: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// --------------------------------------------------------------------
export interface ICreateUpdateCompany {
  company: ICompany;
  company_users: [];
  company_modules: [];
  project_created: boolean;
}

// --------------------------------------------------------------------
export interface IWorkbooklog {
  id?: number;
  user: IUser;
  workbook: IWorkbook;
}

// --------------------------------------------------------------------
export interface ISnackParams {
  message: string;
  title?: string;
  duration?: number;
  config?: MatSnackBarConfig;
}
// --------------------------------------------------------------------

export interface IResponseBody {
  count: number;
  next: string;
  previous: number;
  results: [];
}

export interface IResponseBody {
  count: number;
  next: string;
  previous: number;
  results: [];
}

export interface IDialogSearch {
  subject: string;
}
